import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, ChevronUp, Globe, Search } from "lucide-react";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { changesClocks, currentZoneId, formerZoneId, pickableTimeZones, utcOffsetLabel, zoneLabel } from "@/lib/time-zones";
import { cn } from "@/lib/utils";

/**
 * The Meeting schedule tab's time-zone list (epic PI-SPEC-SW-E294, feature
 * PI-SPEC-SW-1511, frame F1, UX-PM-15): nobody types a zone id. A button
 * showing the zone in words ("Central Time — Chicago") opens a searchable
 * list; each row has the zone's current UTC offset ("UTC−5 now" for a zone
 * that changes its clocks, "UTC+5:30" for one that does not). The zones the
 * project's role holders set in their profiles come first, under "Used by
 * your team" (omitted when there are none).
 *
 * Search matches the words and the id ("cen", "kolkata", "america/chi").
 * Keyboard: arrow keys move, Enter picks, Escape closes.
 */
export interface TimeZonePickerProps {
  /** The picked IANA zone. */
  value: string;
  onChange: (timeZone: string) => void;
  /** The role holders' zones (`useTeamTimeZones`), listed first. */
  teamZones: readonly string[];
  /** "Now", for the offsets. Default: the current time. */
  now?: Date | undefined;
  className?: string;
}

interface ZoneOption {
  id: string;
  label: string;
  offset: string;
  /** Lower-cased words to search. */
  search: string;
}

interface ZoneSection {
  key: "team" | "all";
  heading: string | null;
  options: ZoneOption[];
}

function zoneOption(id: string, now: Date): ZoneOption {
  const label = zoneLabel(id);
  const offset = utcOffsetLabel(id, now);
  return {
    id,
    label,
    offset: offset && changesClocks(id, now) ? `${offset} now` : offset,
    search: `${label} ${id} ${id.replace(/_/g, " ")} ${formerZoneId(id) ?? ""}`.toLowerCase(),
  };
}

/** The zone in words for the button: "Central Time — Chicago". */
export function timeZoneButtonLabel(timeZone: string): string {
  return timeZone ? zoneLabel(timeZone) : "Pick a time zone";
}

export function TimeZonePicker({ value, onChange, teamZones, now: nowProp, className }: TimeZonePickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  /** The row the arrow keys or the mouse moved to; null for the default (see `highlight`). */
  const [moved, setHighlight] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  // The instant the list was opened: offsets are computed once per opening, not on every keystroke.
  const [openedAt, setOpenedAt] = useState(() => Date.now());
  const nowTime = nowProp?.getTime() ?? openedAt;
  // Under today's names (a profile may hold `Asia/Calcutta`), without duplicates.
  const team = useMemo(() => [...new Set(teamZones.map(currentZoneId))], [teamZones]);
  // A stored legacy id (`Asia/Calcutta`) is the same zone as today's name: one row, checked.
  const current = value ? currentZoneId(value) : value;

  /** Every zone with its label and offset, computed once per open. */
  const options = useMemo(() => {
    if (!open) return new Map<string, ZoneOption>();
    const at = new Date(nowTime);
    const ids = new Set([...team, ...pickableTimeZones()]);
    if (current) ids.add(current);
    return new Map([...ids].map((id) => [id, zoneOption(id, at)]));
  }, [open, nowTime, team, current]);

  const sections = useMemo((): ZoneSection[] => {
    const needle = query.trim().toLowerCase();
    const matches = (option: ZoneOption | undefined): option is ZoneOption =>
      option !== undefined && (needle.length === 0 || option.search.includes(needle));
    const teamIds = new Set(team);
    const teamOptions = team.map((id) => options.get(id)).filter(matches);
    const allOptions = [...options.values()]
      .filter((option) => !teamIds.has(option.id))
      .filter(matches)
      .sort((a, b) => a.label.localeCompare(b.label));
    const result: ZoneSection[] = [];
    if (teamOptions.length > 0) result.push({ key: "team", heading: "Used by your team", options: teamOptions });
    if (allOptions.length > 0) {
      result.push({ key: "all", heading: teamOptions.length > 0 ? "All time zones" : null, options: allOptions });
    }
    return result;
  }, [options, query, team]);

  const flat = useMemo(() => sections.flatMap((section) => section.options), [sections]);

  // Until the arrow keys or the mouse move it, a search highlights its first match and an open list the picked zone.
  const picked = query ? -1 : flat.findIndex((option) => option.id === current);
  const highlight = moved ?? Math.max(picked, 0);

  useEffect(() => {
    if (!open) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-index="${String(highlight)}"]`);
    if (typeof row?.scrollIntoView === "function") row.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  const close = () => {
    setOpen(false);
    setHighlight(null);
    setQuery("");
  };

  const pick = (id: string) => {
    onChange(id);
    close();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight(Math.min(highlight + 1, flat.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight(Math.max(highlight - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = flat[highlight];
      if (option) pick(option.id);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
  };

  const activeId = flat[highlight] ? `${listId}-${String(highlight)}` : undefined;
  let index = -1;

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        if (next) setOpenedAt(Date.now());
        setOpen(next);
        setHighlight(null);
        if (!next) setQuery("");
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="Time zone"
          aria-haspopup="listbox"
          aria-expanded={open}
          data-testid="time-zone-picker"
          className={cn(
            "flex h-8 w-[340px] max-w-full items-center gap-2 whitespace-nowrap rounded-lg border bg-[#111113] px-2.5 text-left text-[#FAFAFA]",
            open ? "border-[#23A4FF]" : "border-[#3F3F46] hover:border-[#52525B]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#23A4FF]",
            TOOLBOX_TEXT.content,
            className
          )}
        >
          <Globe className={cn("h-4 w-4 flex-none", TOOLBOX_TEXT.faint)} aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate" data-testid="time-zone-picker-label">
            {timeZoneButtonLabel(value)}
          </span>
          {open ? (
            <ChevronUp className={cn("h-4 w-4 flex-none", TOOLBOX_TEXT.faint)} aria-hidden="true" />
          ) : (
            <ChevronDown className={cn("h-4 w-4 flex-none", TOOLBOX_TEXT.faint)} aria-hidden="true" />
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 flex w-[340px] flex-col rounded-[10px] border border-[#3F3F46] bg-[#18181B] p-1.5 shadow-[0_16px_40px_rgba(0,0,0,.55)] outline-none"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            inputRef.current?.focus();
          }}
          data-testid="time-zone-list"
        >
          <label className="mb-1.5 flex h-[30px] items-center gap-2 rounded-lg border border-[#27272A] bg-[#111113] px-2.5">
            <Search className={cn("h-4 w-4 flex-none", TOOLBOX_TEXT.faint)} aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-label="Search time zones"
              aria-controls={listId}
              aria-expanded="true"
              aria-activedescendant={activeId}
              placeholder="Search a city or zone"
              className={cn("min-w-0 flex-1 bg-transparent text-[#D4D4D8] outline-none placeholder:text-[#8B8B94]", TOOLBOX_TEXT.content)}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setHighlight(null);
              }}
              onKeyDown={onKeyDown}
            />
          </label>
          <div ref={listRef} id={listId} role="listbox" aria-label="Time zones" className="max-h-[280px] overflow-y-auto">
            {flat.length === 0 && (
              <p className={cn("px-2 py-3", TOOLBOX_TEXT.content, "text-muted-foreground")} data-testid="time-zone-none">
                No time zone matches “{query.trim()}”.
              </p>
            )}
            {sections.map((section) => (
              <div key={section.key} role="group" aria-label={section.heading ?? "Time zones"} data-testid={`time-zone-group-${section.key}`}>
                {section.heading && <div className={cn(TOOLBOX_TEXT.eyebrow, "px-2 pb-1 pt-2")}>{section.heading}</div>}
                {section.options.map((option) => {
                  index += 1;
                  const rowIndex = index;
                  const selected = option.id === current;
                  const highlighted = rowIndex === highlight;
                  return (
                    <div
                      key={option.id}
                      id={`${listId}-${String(rowIndex)}`}
                      role="option"
                      aria-selected={selected}
                      data-index={rowIndex}
                      data-zone={option.id}
                      data-highlighted={highlighted || undefined}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-md px-2 py-[7px]",
                        TOOLBOX_TEXT.content,
                        highlighted ? "bg-[#27272A] text-[#FAFAFA]" : "text-[#D4D4D8]"
                      )}
                      onMouseEnter={() => {
                        setHighlight(rowIndex);
                      }}
                      onMouseDown={(event) => {
                        event.preventDefault();
                      }}
                      onClick={() => {
                        pick(option.id);
                      }}
                    >
                      <span className="flex w-3.5 flex-none justify-center">
                        {selected && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{option.label}</span>
                      <span className={cn(TOOLBOX_TEXT.meta, "flex-none text-muted-foreground")}>{option.offset}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
